import { NuxtError } from 'nuxt/app'
import { ErrorCode } from '~/static/enums/error-code'
import { ErrorResponse, ResponseResult } from '~/static/types/base'
import queryString from 'query-string'

export const isEmptyObject = (object: object) => {
  return Object.keys(object).length === 0
}

export const stringifyGetParams = (params: object): string => {
  return queryString.stringify(params, {
    arrayFormat: 'bracket',
    skipNull: true,
  })
}

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
}

const errorsHandling = function (ctx: {
  response: (Response & {
    _data?: {
      error: ErrorResponse
      message?: string
      errors?: string[]
    }
  }) | null
}) {
  if (ctx.response?.status === ErrorCode.PageNotFound) {
    throw createError({ statusCode: ErrorCode.PageNotFound, statusMessage: 'Page not found' })
  }
  if (ctx.response?.status === ErrorCode.ServerError) {
    throw createError({ statusCode: ErrorCode.ServerError, statusMessage: 'Server error' })
  }
  if (ctx.response?.status === ErrorCode.Unauthorized) {
    throw createError({ statusCode: ErrorCode.ServerError, statusMessage: 'Unauthorized' })
  }
  throw createError({
    statusCode: ctx.response?.status,
    statusMessage: ctx.response?._data?.error?.error_msg ?? ctx.response?._data?.message,
    data: ctx.response?._data?.error?.errors ?? ctx.response?._data?.errors,
  })
}

const createUrl = (path: string, queryParams: { [key: string ]: unknown }): string => {
  const config = useRuntimeConfig()
  const url: URL = new URL(config.public.baseApiUrl + '/' + path)
  if (!isEmptyObject(queryParams)) {
    url.search = stringifyGetParams(queryParams)
  }
  return url.toString()
}

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  return {
    name: 'api',
    enforce: 'pre',
    provide: {
      useApi: async <R = unknown, E = object> (
        path: string,
        queryParams: { [key: string ]: unknown } = {},
        method: HttpMethod = HttpMethod.GET,
        body: object | null = null,
      ): Promise<ResponseResult<R, E>> => {
        const url: string = createUrl(path, queryParams)

        const headers: HeadersInit = {
          Origin: config.public.appUrl,
          Referer: config.public.appUrl,
          Authorization: `Bearer ${ useCookie('token').value }`,
          ati: <string>useCookie('amplitude_track_id').value,
        }

        let fetchBody: BodyInit | undefined = undefined
        if (method === HttpMethod.POST && body !== null) {
          if (body.constructor === FormData) {
            fetchBody = body
          } else {
            fetchBody = JSON.stringify(body)
            headers['Content-Type'] = 'application/json'
          }
        }

        let response: R
        try {
          response = await $fetch<R>(url, {
            baseURL: config.public.baseApiUrl,
            credentials: 'include',
            method,
            headers,
            body: fetchBody,
            onResponseError: (ctx) => {
              return errorsHandling(ctx)
            },
          })
        } catch (e: unknown) {
          if (isNuxtError(<object>e)) {
            const nuxtError = <NuxtError>e
            if (nuxtError.statusCode !== ErrorCode.PageNotFound) {
              return {
                response: undefined,
                error: {
                  error_code: nuxtError.statusCode,
                  error_msg: nuxtError.statusMessage ?? nuxtError.message,
                  errors: nuxtError.data,
                },
              }
            }
          }
          throw e
        }

        return {
          response: response,
          error: undefined,
        }
      },
    },
  }
})
